"use client";

import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useCallback,
} from "react";
import { useAuth } from "@/context/auth-context";
import {
    connectToNotificationHub,
    disconnectFromNotificationHub,
    getConnectionStatus,
} from "@/services/notificationSignalR";
import logger from "@/utils/logger";

/**
 * Singleton SignalR Provider
 *
 * This provider manages a single SignalR connection for the entire app.
 * Components can register handlers via context without creating duplicate connections.
 *
 * IMPORTANT: This provider should be placed at the ROOT of the app, above all routing logic.
 * It should mount once and stay mounted for the entire app lifecycle.
 */

interface SignalRContextType {
    isConnected: boolean;
    isConnecting: boolean;
    registerNotificationHandler: (
        handlerName: string,
        handler: (notification: unknown) => void
    ) => () => void; // Returns unregister function
    registerTripStatusHandler: (
        handlerName: string,
        handler: (data: TripStatusChangedData) => void
    ) => () => void; // Returns unregister function
}

interface TripStatusChangedData {
    tripId: number;
    newStatus: string;
    oldStatus: string | null;
    changedAt: string;
    changedBy?: number;
}

const SignalRContext = createContext<SignalRContextType | null>(null);

export function useSignalR() {
    const context = useContext(SignalRContext);
    if (!context) {
        throw new Error("useSignalR must be used within SignalRProvider");
    }
    return context;
}

export function SignalRProvider({ children }: { children: React.ReactNode }) {
    const { userProfile, externalUserProfile, loginType } = useAuth();
    const connectionInitialized = useRef(false);
    const handlersRegistered = useRef(new Set<string>());

    // Get current user email (internal or external)
    const currentUserEmail =
        loginType === "msal" ? userProfile?.email : externalUserProfile?.email;

    // Derive a single role string for SignalR role-aware routing.
    // Priority: Admin > Clinical Services > first available role > empty.
    const userRoles =
        loginType === "msal"
            ? (userProfile?.roles ?? [])
            : (externalUserProfile?.roles ?? []);
    const currentUserRole = (() => {
        if (userRoles.some((r) => r.toLowerCase() === "admin")) return "Admin";
        if (userRoles.some((r) => r.toLowerCase() === "clinical services"))
            return "Clinical Services";
        return userRoles[0] ?? "";
    })();

    logger.info(
        "🔌 [SignalRProvider] Initializing with user:",
        currentUserEmail,
        "| Login type:",
        loginType
    );

    // Initialize connection once when email is available
    useEffect(() => {
        const handlersToClean = handlersRegistered.current;
        let unregisterRootConnection: () => void = () => {};

        if (!currentUserEmail || !currentUserEmail.includes("@")) {
            logger.info(
                "🔌 [SignalRProvider] No valid email, skipping connection"
            );
            return;
        }

        if (connectionInitialized.current) {
            logger.info("🔌 [SignalRProvider] Connection already initialized");
            return;
        }

        logger.info(
            "🔌 [SignalRProvider] Establishing SignalR connection for:",
            currentUserEmail
        );

        unregisterRootConnection = connectToNotificationHub(
            currentUserEmail,
            () => {}, // Placeholder - real handlers registered via context
            undefined,
            "SignalRProvider-Root",
            currentUserRole
        );

        connectionInitialized.current = true;

        // Cleanup only on unmount (should never happen in production)
        return () => {
            logger.info(
                "🔌 [SignalRProvider] Disconnecting (should only happen on app close)"
            );
            unregisterRootConnection();
            void disconnectFromNotificationHub();
            connectionInitialized.current = false;
            handlersToClean.clear();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUserEmail]);

    // Register a notification handler (can be called by any component)
    const registerNotificationHandler = useCallback(
        (handlerName: string, handler: (notification: unknown) => void) => {
            logger.info(
                `🔌🔌🔌 [SignalRProvider] registerNotificationHandler CALLED with name: "${handlerName}"`
            );
            logger.info(
                `🔌 [SignalRProvider] Current user email: ${currentUserEmail}`
            );
            logger.info(`🔌 [SignalRProvider] Handler type: ${typeof handler}`);

            if (!currentUserEmail) {
                logger.warn(
                    `🔌 [SignalRProvider] Cannot register handler "${handlerName}" - no email`
                );
                return () => {}; // No-op unregister
            }

            logger.info(
                `🔌 [SignalRProvider] Registering notification handler: ${handlerName}`
            );

            // Register handler with SignalR service
            logger.info(
                `🔌 [SignalRProvider] Calling connectToNotificationHub for "${handlerName}"`
            );
            const unregisterSignalRHandler = connectToNotificationHub(
                currentUserEmail,
                handler,
                undefined,
                handlerName,
                currentUserRole
            );
            logger.info(
                `🔌 [SignalRProvider] connectToNotificationHub returned for "${handlerName}"`
            );

            handlersRegistered.current.add(handlerName);
            logger.info(
                `🔌 [SignalRProvider] Added "${handlerName}" to handlersRegistered set (size: ${handlersRegistered.current.size})`
            );

            // Return unregister function
            return () => {
                logger.info(
                    `🔌 [SignalRProvider] Unregistering notification handler: ${handlerName}`
                );
                handlersRegistered.current.delete(handlerName);
                unregisterSignalRHandler();
            };
        },
        [currentUserEmail, currentUserRole]
    );

    // Register a trip status change handler
    const registerTripStatusHandler = useCallback(
        (
            handlerName: string,
            handler: (data: TripStatusChangedData) => void
        ) => {
            if (!currentUserEmail) {
                logger.warn(
                    `🔌 [SignalRProvider] Cannot register handler "${handlerName}" - no email`
                );
                return () => {}; // No-op unregister
            }

            logger.info(
                `🔌 [SignalRProvider] Registering trip status handler: ${handlerName}`
            );

            // Register handler with SignalR service
            const unregisterSignalRHandler = connectToNotificationHub(
                currentUserEmail,
                () => {}, // No notification handler
                handler,
                handlerName,
                currentUserRole
            );

            handlersRegistered.current.add(handlerName);

            // Return unregister function
            return () => {
                logger.info(
                    `🔌 [SignalRProvider] Unregistering trip status handler: ${handlerName}`
                );
                handlersRegistered.current.delete(handlerName);
                unregisterSignalRHandler();
            };
        },
        [currentUserEmail, currentUserRole]
    );

    const status = getConnectionStatus();

    const contextValue: SignalRContextType = {
        isConnected: status.isConnected,
        isConnecting: status.isConnecting,
        registerNotificationHandler,
        registerTripStatusHandler,
    };

    return (
        <SignalRContext.Provider value={contextValue}>
            {children}
        </SignalRContext.Provider>
    );
}
